/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import CustomMap from './map';
import { useLazyQuery } from '@apollo/client';
import {
  GET_CABINET_ENTITIES_BY_USER,
  GET_ALL_NODES,
  TCabinetEntity,
  TGetCabinetEntitiesByUserOutput,
  TGetAllNodesQueryOutput,
  TGetAllNodesOutput,
  TGetCabinetParamDataOutput,
  TGetCabinetDataInput,
  GET_CABINET_PARAM
} from '../../../graphql/schema';
import DeviceDetailModal from './DeviceDetailModal';
import NodeDetailPanel from './NodeDetailPanel';
import { CabinetContext } from '../../../context/CabinetContext';
import { DEFAULT_CENTER, eDeviceState, FAKE_CABINET_ADDRESS, FAKE_CABINET_IDENTIFIER, FAKE_CABINET_NAME } from '../../../common/constants';
import { CPoint } from '../../../common/type';
import { generateRandomCoordinates } from '../../../utils/utils';
import { useAuth } from '../../../context/AuthContext';

interface ITrackingPageProps {
}

export interface ICabinetInfo extends TCabinetEntity {
  identifier: string;
  name: string;
  currentState: eDeviceState;
  location: CPoint;
  address: string;
}

export interface INodeInfo extends TGetAllNodesOutput {
  currentState: eDeviceState;
}

const TrackingPage: React.FunctionComponent<ITrackingPageProps> = (props) => {
  const [deviceList, setDeviceList] = React.useState<ICabinetInfo[]>([])
  const [nodeList, setNodeList] = React.useState<INodeInfo[]>([])
  const { setSelectedDevice, selectedDevice } = React.useContext(CabinetContext)!
  const [isModalVisible, setIsModalVisible] = React.useState<boolean>(false);
  const [isNodePanelVisible, setIsNodePanelVisible] = React.useState<boolean>(false);
  const [selectedNode, setSelectedNode] = React.useState<INodeInfo | null>(null);
  const [getCabinetEntities] = useLazyQuery<TGetCabinetEntitiesByUserOutput>(GET_CABINET_ENTITIES_BY_USER, {
    fetchPolicy: 'network-only',
  });
  const [getAllNodes] = useLazyQuery<TGetAllNodesQueryOutput>(GET_ALL_NODES, {
    fetchPolicy: 'network-only',
  });

  const [getCabinetParam] = useLazyQuery<TGetCabinetParamDataOutput, TGetCabinetDataInput>(GET_CABINET_PARAM, {
    fetchPolicy: 'network-only',
  });
  const authContext = useAuth();

  const getEntities = async () => {
    const returnData = await getCabinetEntities();
    const result = returnData.data?.getCabinetEntities;
    
    if (result) {
      const temp: ICabinetInfo[] = []
      for (const item of result) {
        const returnDataParam = await getCabinetParam({
          variables: {
            cabinetDeviceId: item.id
          }
        });
        const resultParam = returnDataParam.data?.getCabinetParamData;
        if (!resultParam) continue
        const location = resultParam.lat && resultParam.lng ? [resultParam.lng, resultParam.lat] : [DEFAULT_CENTER[1], DEFAULT_CENTER[0]]
        if (authContext?.userInfo?.user.location?.coordinates) {
          authContext.userInfo.user.location.coordinates = location
        }
        temp.push({
          ...item,
          identifier: FAKE_CABINET_IDENTIFIER + item.id,
          name: FAKE_CABINET_NAME + item.id,
          address: FAKE_CABINET_ADDRESS,
          currentState: eDeviceState.NORMAL,
          location: {
            coordinates: location
          }
        })
      }
      setDeviceList(temp)
    }
  }

  const getNodesByCabinet = async (cabinetId: number) => {
    try {
      const returnData = await getAllNodes({ variables: { cabinetId } });
      const result = returnData.data?.getAllNodes;
      if (result) {
        setNodeList(result.map(item => ({
          ...item,
          currentState: eDeviceState.NORMAL,
          nodeParams: {
            ...item.nodeParams,
            location: {
              coordinates: generateRandomCoordinates(DEFAULT_CENTER as [number, number])
            }
          }
        })))
      } else {
        setNodeList([])
      }
    } catch (error) {
      console.error('Error fetching nodes for cabinet:', error);
    }
  }

  React.useEffect(() => {
    getEntities()
  }, [])

  const handleClick = (cabinet: ICabinetInfo) => {
    setSelectedDevice(cabinet)
    setIsModalVisible(true)
    setIsNodePanelVisible(false) // Close node panel when cabinet is clicked
    setSelectedNode(null)
    // Fetch nodes for the selected cabinet
    getNodesByCabinet(cabinet.id)
  }

  const handleNodeClick = (node: INodeInfo) => {
    setSelectedNode(node)
    setIsNodePanelVisible(true)
    setIsModalVisible(false) // Close cabinet modal when node is clicked
  }

  return <div className='relative'>
    {selectedDevice && <DeviceDetailModal isShow={isModalVisible} handleCancel={() => setIsModalVisible(false)} nodeList={nodeList} />}
    <NodeDetailPanel
      isShow={isNodePanelVisible}
      selectedNode={selectedNode}
      handleCancel={() => {
        setIsNodePanelVisible(false)
        setSelectedNode(null)
      }}
    />
    <CustomMap
      data={deviceList}
      nodeData={nodeList}
      handleClick={handleClick}
      handleNodeClick={handleNodeClick}
      isModalOpen={isModalVisible || isNodePanelVisible}
    />
  </div>;
};

export default TrackingPage;
